/**
 * @file SuggestionsTab.tsx
 * @description Displays related/suggested content for the current title
 *
 * Architecture:
 * - Uses CollectionRail component with device-aware card types
 * - Phone Portrait: VERTICAL layout (portrait 2:3 cards for thumb navigation)
 * - Tablet Portrait/Landscape/Desktop: HORIZONTAL layout (landscape 16:9 cards)
 * - Uses canonical DEVICE system for orientation-aware decisions
 * - Fetches from a suggestions/related collection (placeholder for now)
 *
 * Future Enhancement:
 * - Implement recommendation algorithm (genre, country, director matching)
 * - Add user-based collaborative filtering
 * - Track engagement metrics
 */

import type { ApiTitle } from "@lumi/data";
import type { Variants } from "framer-motion";
import { useEffect, useState, type ReactElement } from "react";
import { apiCall } from "../../../config/api";
import { DEVICE, VIEWPORT } from "../../../shared/constants/ui";
import { CollectionRail } from "../../home/components/CollectionRail";

export interface SuggestionsTabProps {
  /** Current title ID (for future recommendation logic) */
  readonly titleId: string;
  /** Navigation function with transition support */
  readonly transitionTo: (path: string, variant?: Variants) => void;
}

interface CollectionResponse {
  items: Array<{
    rank: number;
    title: ApiTitle;
  }>;
}

/**
 * SuggestionsTab - displays related content recommendations
 *
 * Current implementation uses "new-releases" as placeholder.
 * Future: Replace with actual recommendation algorithm based on titleId
 */
export function SuggestionsTab({ titleId, transitionTo }: SuggestionsTabProps): ReactElement {
  const [titles, setTitles] = useState<ApiTitle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine card orientation based on device category
  // Phones (portrait): Vertical cards for easier thumb navigation
  // Portrait tablets + Landscape/Desktop: Horizontal cards (larger screens)
  const { width, height } = VIEWPORT.getDimensions();
  const isPhonePortrait = DEVICE.isPhonePortrait(width, height);
  const cardType = isPhonePortrait ? "VERTICAL" : "HORIZONTAL";

  useEffect(() => {
    async function loadSuggestions(): Promise<void> {
      try {
        // Future: Replace with dedicated suggestions endpoint
        // e.g., `/v1/titles/${titleId}/suggestions`
        // For now, use new-releases as placeholder
        const response = await apiCall("/v1/collections/new-releases");

        if (!response.ok) {
          throw new Error(`Failed to load suggestions: ${response.status}`);
        }

        const data = (await response.json()) as CollectionResponse;

        // Filter out the current title from suggestions
        const suggestions = data.items
          .map((item) => item.title)
          .filter((title) => title.id !== titleId)
          .slice(0, 12); // Limit to 12 suggestions

        setTitles(suggestions);
      } catch (err) {
        console.error("❌ Failed to load suggestions:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    }

    void loadSuggestions();
  }, [titleId]);

  // Show error state
  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-neutral-400">Unable to load suggestions</p>
      </div>
    );
  }

  // Show empty state if no suggestions
  if (!loading && titles.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-neutral-400">No suggestions available</p>
      </div>
    );
  }

  return (
    <div className="pt-0 md:pt-0 lg:pt-2">
      <CollectionRail
        title=""
        items={titles}
        cardType={cardType}
        loading={loading}
        transitionTo={transitionTo}
        hideActions={true}
        preferInfoOnEnter={true}
        hideDetails={true}
      />
    </div>
  );
}
